import { motion } from 'motion/react';

interface PersonalizedScreeningInterstitialProps {
  sex?: string;
}

// ─── Timeline data ──────────────────────────────────────────────────────────

interface TimelineItem {
  test: string;
  reason: string;
}

function getTimeline(sex: string | undefined): TimelineItem[] {
  const isMale = sex === 'male';

  return [
    { test: 'At home FIT test', reason: 'Based on elevated colon cancer risk' },
    isMale
      ? { test: 'PSA blood test', reason: 'Based on your age and prostate cancer risk' }
      : { test: '3D Mammogram', reason: 'Based on your prior breast exams' },
    { test: 'Genetic testing', reason: 'Based on your family history' },
  ];
}

// ─── Component ──────────────────────────────────────────────────────────────

export function PersonalizedScreeningInterstitial({ sex }: PersonalizedScreeningInterstitialProps) {
  const timeline = getTimeline(sex);

  return (
    <div className="flex h-full w-full flex-col items-center justify-center px-6">
      <motion.div
        className="flex w-full max-w-md flex-col gap-6"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
        style={{ willChange: 'transform, opacity' }}
      >
        {/* Headline */}
        <h3 className="text-center text-2xl font-bold leading-tight text-white md:text-3xl">
          Generic screening is one-size-fits-all.
        </h3>

        {/* Subtitle */}
        <p className="mx-auto max-w-sm text-center text-lg leading-relaxed text-white/65">
          Catch tailors your screening plan to your unique risk and gives you a doctor-ready summary.
        </p>

        {/* Timeline */}
        <div className="mx-auto mt-4 w-full max-w-sm space-y-0">
          {timeline.map((item, i) => (
            <motion.div
              key={item.test}
              className="flex items-start gap-4"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.6 + i * 0.5, ease: 'easeOut' }}
              style={{ willChange: 'transform, opacity' }}
            >
              {/* Dot + connecting line */}
              <div className="flex flex-col items-center">
                <div className="mt-1.5 h-3 w-3 shrink-0 rounded-full bg-[#7CF1C7]" />
                {i < timeline.length - 1 && <div className="h-12 w-0.5 bg-[#7CF1C7]/30" />}
              </div>

              {/* Content */}
              <div className="pb-6 text-left">
                <p className="text-base font-semibold text-white">{item.test}</p>
                <span className="mt-1 inline-flex items-center gap-1 rounded-full bg-[#7CF1C7]/15 px-2 py-0.5 text-xs text-[#7CF1C7]">
                  &#x2713; {item.reason}
                </span>
              </div>
            </motion.div>
          ))}
        </div>
      </motion.div>
    </div>
  );
}
